import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import {
  type LotoOtherPage,
  type LotoPointChanges,
  type LotoPointKey,
} from 'features/lotoReports/lotoIsolationPoints'
import type { LotoIsolationPoint } from 'features/lotoReports/lotoReportSchemas'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import type { ReactElement } from 'react'

import {
  LotoPointActions,
  LotoPointDetail,
  LotoPointFocusButton,
  LotoPointLead,
  LotoPointOrder,
  LotoPointPageLink,
  LotoPointPosition,
  LotoPointRemoveSlot,
  LotoPointRowGrid,
  LotoPointSecondary,
  LotoPointTagHeading,
  LotoPointToggleSlot,
  LotoPointValve,
  LotoVisuallyHidden,
} from './lotoPagePanel.styles'
import { LotoPointPositionCombobox } from './LotoPointPositionCombobox'
import { LotoPointDragHandle, useLotoSortablePoint } from './LotoSortablePoints'

type OtherPageLocation = {
  kind: 'otherPage'
  page: LotoOtherPage | undefined
  loading: boolean
}

export type LotoPointLocation = { kind: 'thisPage' } | OtherPageLocation

export type LotoPointRowActions = {
  onChange: (key: LotoPointKey, changes: LotoPointChanges) => void
  onRemove: (key: LotoPointKey) => void
  onMove: (from: number, to: number) => void
  onOpenPage: (page: LotoOtherPage) => void
  onFocus: (key: LotoPointKey, page?: LotoOtherPage) => void
}

type LotoIsolationPointRowProps = {
  point: LotoIsolationPoint
  index: number
  location: LotoPointLocation
  expanded: boolean
  onToggleExpanded: (() => void) | undefined
  actions: LotoPointRowActions
}

function PointLocation({
  location,
  onOpenPage,
}: {
  location: OtherPageLocation
  onOpenPage: (page: LotoOtherPage) => void
}): ReactElement {
  const { t } = useLingui()
  const { page, loading } = location
  const pageNumber = page?.target?.pageNumber
  if (!page || pageNumber === undefined)
    return (
      <Text as='span' variant='caption' color='muted' truncate>
        {loading ? (
          <Trans>Loading page…</Trans>
        ) : (
          <Trans>Page unavailable</Trans>
        )}
      </Text>
    )
  const document = page.documentName ?? t`Untitled document`
  const label = t`${document} p.${pageNumber}`
  return (
    <LotoPointPageLink
      type='button'
      aria-label={t`Open ${document} page ${pageNumber}`}
      title={label}
      onClick={() => onOpenPage(page)}>
      <Text as='span' variant='caption' color='inherit' truncate>
        {label}
      </Text>
    </LotoPointPageLink>
  )
}

function PointPosition({
  point,
  tag,
  onChange,
}: {
  point: LotoIsolationPoint
  tag: string
  onChange: (changes: LotoPointChanges) => void
}): ReactElement {
  return (
    <LotoPointPosition>
      <LotoPointPositionCombobox
        tag={tag}
        value={point.isolation_position}
        onChange={(value) => onChange({ isolation_position: value })}
      />
    </LotoPointPosition>
  )
}

function focusablePage(location: LotoPointLocation): {
  focusable: boolean
  page: LotoOtherPage | undefined
} {
  if (location.kind === 'thisPage') return { focusable: true, page: undefined }
  const { page } = location
  return { focusable: page?.target !== undefined, page }
}

function PointTag({
  tag,
  location,
  muted,
  onFocus,
}: {
  tag: string
  location: LotoPointLocation
  muted: boolean
  onFocus: (page: LotoOtherPage | undefined) => void
}): ReactElement {
  const { t } = useLingui()
  const { focusable, page } = focusablePage(location)
  return (
    <LotoPointTagHeading>
      <LotoPointFocusButton
        type='button'
        aria-label={t`Focus ${tag} on the drawing`}
        disabled={!focusable}
        onClick={() => onFocus(page)}>
        <Text
          as='span'
          size='sm'
          weight={muted ? 'normal' : 'semibold'}
          color={muted ? 'muted' : undefined}>
          {tag}
        </Text>
      </LotoPointFocusButton>
    </LotoPointTagHeading>
  )
}

function RowActions({
  tag,
  expanded,
  onToggleExpanded,
  onRemove,
}: {
  tag: string
  expanded: boolean
  onToggleExpanded: (() => void) | undefined
  onRemove: (() => void) | undefined
}): ReactElement {
  const { t } = useLingui()
  return (
    <LotoPointActions>
      {onRemove && (
        <LotoPointRemoveSlot>
          <Button
            variant='ghost'
            size='icon-sm'
            aria-label={t`Remove ${tag}`}
            onClick={onRemove}>
            <X />
          </Button>
        </LotoPointRemoveSlot>
      )}
      {onToggleExpanded && (
        <LotoPointToggleSlot>
          <Button
            variant='ghost'
            size='icon-sm'
            aria-label={t`Details for ${tag}`}
            aria-expanded={expanded}
            onClick={onToggleExpanded}>
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </Button>
        </LotoPointToggleSlot>
      )}
    </LotoPointActions>
  )
}

function pointLabel(
  point: LotoIsolationPoint,
  fallback: string
): { label: string; detail: string } {
  const tag = point.tag.trim()
  const description = point.description.trim()
  if (tag) return { label: tag, detail: description }
  return { label: description || fallback, detail: '' }
}

function orderTone(
  onThisPage: boolean,
  collapsed: boolean
): 'accent' | 'muted' | 'default' {
  if (onThisPage) return 'accent'
  return collapsed ? 'muted' : 'default'
}

export function LotoIsolationPointRow({
  point,
  index,
  location,
  expanded,
  onToggleExpanded,
  actions,
}: LotoIsolationPointRowProps): ReactElement {
  const { t } = useLingui()
  const { label: tag, detail } = pointLabel(point, t`Unnamed point`)
  const key = { pageId: point.page_id, nodeId: point.node_id }
  const collapsed = !expanded
  const onThisPage = location.kind === 'thisPage'
  const { setNodeRef, transform, transition, isDragging, handle } =
    useLotoSortablePoint(point, 'free')
  return (
    <LotoPointRowGrid
      ref={setNodeRef}
      $onThisPage={onThisPage}
      $collapsed={collapsed}
      $dragging={isDragging}
      $transform={transform}
      $transition={transition}>
      <LotoPointLead>
        <LotoPointDragHandle tag={tag} handle={handle} />
        <LotoPointOrder aria-hidden $tone={orderTone(onThisPage, collapsed)}>
          {index + 1}
        </LotoPointOrder>
        {onThisPage && (
          <LotoVisuallyHidden>
            <Trans>This page</Trans>
          </LotoVisuallyHidden>
        )}
      </LotoPointLead>
      <LotoPointValve>
        <PointTag
          tag={tag}
          location={location}
          muted={collapsed}
          onFocus={(page) => actions.onFocus(key, page)}
        />
        {location.kind === 'otherPage' && (
          <LotoPointSecondary>
            <PointLocation
              location={location}
              onOpenPage={actions.onOpenPage}
            />
          </LotoPointSecondary>
        )}
      </LotoPointValve>
      {expanded && detail && (
        <LotoPointDetail>
          <Text variant='caption' color='muted' truncate title={detail}>
            {detail}
          </Text>
        </LotoPointDetail>
      )}
      {expanded && (
        <PointPosition
          point={point}
          tag={tag}
          onChange={(changes) => actions.onChange(key, changes)}
        />
      )}
      <RowActions
        tag={tag}
        expanded={expanded}
        onToggleExpanded={onToggleExpanded}
        onRemove={collapsed ? undefined : () => actions.onRemove(key)}
      />
    </LotoPointRowGrid>
  )
}
